import { Box, Flex, Stack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { VictoryPie, VictoryTooltip } from 'victory';

import {
  CreateMonthlyReport,
  CreateReportExpenseFormData,
  CreateReportPurchaseFormData,
  CreateSingleReport
} from '../../models';
import { currencyFormatter } from '../../utils';
import { chartColors, Legend } from '../Graph';

type MonthlyExpensesProps = CreateReportExpenseFormData &
  CreateSingleReport &
  CreateReportPurchaseFormData &
  CreateMonthlyReport;

interface PieChartData {
  x: string;
  y: number;
}

export function MonthlyExpensesChart(props: MonthlyExpensesProps) {
  const [utilities, setUtilities] = useState<PieChartData>();
  const [maintenance, setMaintenance] = useState<PieChartData>();
  const [monthlyPayment, setMonthlyPayment] = useState<PieChartData>();
  const [monthlyFees, setMonthlyFees] = useState<PieChartData>();
  const [capitalExpenditures, setCapitalExpenditures] = useState<PieChartData>();
  const [monthlyRevAvg, setMonthlyRevAvg] = useState<number>(0);
  const [monthlyTotal, setMonthlyTotal] = useState<number>(0);

  useEffect(() => {
    if (props.monthlyRevenues) {
      setMonthlyRevAvg(
        props.monthlyRevenues.reduce((a, b) => a + b, 0) / props.monthlyRevenues.length
      );
    }

    setUtilities({
      x: 'Utilities',
      y: sumUtilities(props.electricity, props.gas, props.utilities, props.internet)
    });
    setMaintenance({
      x: 'Maintenance',
      y: sumMaintenance(props.maintenance, props.landscaping, props.snowRemoval, props.pestControl)
    });
    setMonthlyPayment({
      x: 'Monthly Payment',
      y: props.monthlyPaymentWithTaxesAndInsurance
    });
    setMonthlyFees({
      x: 'Monthly Fees',
      y: sumFees(
        props.cleaningFees * props.cleansPerMonth,
        props.legalFees,
        props.permitAndLicenseFees,
        props.managementFees,
        props.bookingPlatformFees,
        monthlyRevAvg
      )
    });
    setCapitalExpenditures({
      x: 'Capital Expenditures',
      y: props.capitalExpenditures * 0.01 * monthlyRevAvg || 0
    });
    setMonthlyTotal(
      (utilities?.y || 0) +
        (maintenance?.y || 0) +
        (monthlyPayment?.y || 0) +
        (monthlyFees?.y || 0) +
        (capitalExpenditures?.y || 0)
    );
  }, [
    props,
    monthlyRevAvg,
    utilities,
    maintenance,
    monthlyPayment,
    monthlyFees,
    capitalExpenditures
  ]);

  return (
    <>
      {utilities && maintenance && monthlyPayment ? (
        <>
          <VStack fontFamily="Montserrat" color="#5A5A5A" mt="20">
            <Text fontSize="2xl" fontWeight="bold">
              Monthly Expense Breakdown
            </Text>
            <Legend
              labels={[
                'Utilities',
                'Maintenance',
                'Monthly Payment',
                'Monthly Fees',
                'Capital Expenditures'
              ]}
            />
          </VStack>
          <VStack justify="center" align="center" mt="10">
            <Text fontSize="2xl" fontFamily="Montserrat" color="#5A5A5A" fontWeight="bold">
              {`Total: ${currencyFormatter(monthlyTotal)}`}
            </Text>
          </VStack>
          <Flex justify="center" align="center" fontFamily="Montserrat" color="#5A5A5A">
            <Flex>
              <Box h="2xl" w="full">
                <VictoryPie
                  innerRadius={100}
                  data={[utilities, maintenance, monthlyPayment, monthlyFees, capitalExpenditures]}
                  colorScale={chartColors}
                  labels={({ datum }) => `${datum.x}: ${currencyFormatter(datum.y)}`}
                  labelComponent={
                    <VictoryTooltip flyoutWidth={150} dy={0} style={{ fontSize: 10 }} />
                  }
                  style={{
                    labels: { fill: '#5A5A5A' }
                  }}
                />
              </Box>
            </Flex>
            <Flex>
              <Box>
                <Stack>
                  <Text fontWeight="bold">Utilities</Text>
                  <Text>Electricity | Gas | Water | Sewer | Garbage | Internet</Text>
                  <Text fontWeight="bold">Maintenance </Text>
                  <Text>General Maintenance | Landscaping | Snow Removal | Pest Control</Text>
                  <Text fontWeight="bold">Monthly payment </Text>
                  <Text>Principal | Interest | Taxes | Insurance</Text>
                  <Text fontWeight="bold">Fees</Text>
                  <Text>Cleaning | Management | Booking Platform | Legal | Permit and License</Text>
                  <Text fontWeight="bold">Capital Expenditures</Text>
                </Stack>
              </Box>
            </Flex>
          </Flex>
        </>
      ) : null}
    </>
  );
}

const sumUtilities = (electricity: number, gas: number, utils: number, internet: number) => {
  return (electricity || 0) + (gas || 0) + (utils || 0) + (internet || 0);
};

const sumMaintenance = (
  maintenance: number,
  landscaping: number,
  snow: number,
  pestControl: number
) => {
  return (maintenance || 0) + (landscaping || 0) + (snow || 0) + (pestControl || 0);
};

const sumFees = (
  cleaningCost: number,
  legalFee: number,
  permitFee: number,
  managementFee: number,
  bookingFee: number,
  monthlyRevAvg: number
) => {
  return (
    (cleaningCost || 0) +
    (legalFee || 0) +
    (permitFee || 0) +
    (managementFee * 0.01 * monthlyRevAvg || 0) +
    (bookingFee * 0.01 * monthlyRevAvg || 0)
  );
};

export default MonthlyExpensesChart;
